import * as Combobox from '@givebutter/primitives/combobox';
import * as SingleSelect from '@givebutter/primitives/single-select';
import { faHashtag } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { BranchFormValues } from 'assets/js/pages/engage/workflows/actions/types';
import { CampaignSelect } from 'assets/js/pages/engage/workflows/components/CampaignSelect';
import { MessageSelect } from 'assets/js/pages/engage/workflows/components/MessageSelect';
import * as Form from 'components/form';
import { BranchConditionEnum } from 'constants/workflows';
import { useCurrentAccountTags } from 'customHooks/useCurrentAccountTags';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { sortBy } from 'lodash';

const BranchFormValueInput = () => {
    const { workflowBranchIncludeAllTime } = useFlags();
    const { control, watch } = Form.useGivebutterFormContext<BranchFormValues>();
    const [condition] = watch(['condition']);

    const { data: accountContactTags = [] } = useCurrentAccountTags();
    const availableTags = sortBy(
        accountContactTags.map(tag => tag.name),
        t => t.toLowerCase(),
    );

    switch (condition) {
        case BranchConditionEnum.MESSAGES_OPEN:
        case BranchConditionEnum.MESSAGES_CLICK:
            return <MessageSelect condition={condition} />;
        case BranchConditionEnum.AUCTION_REGISTERED_TO_BID:
            return (
                <div className="flex flex-col gap-4">
                    <Form.Container>
                        <CampaignSelect path="value.campaigns" resource="auction-campaigns" />
                    </Form.Container>
                    <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                        {workflowBranchIncludeAllTime ? (
                            <>
                                <p className="text-sm font-medium">⚙️ Configure</p>
                                <p className="text-xs text-gray-600">
                                    This applies to registered bidders in the campaigns selected
                                    after the contact has entered the workflow.
                                </p>

                                <div className="flex items-center gap-2">
                                    <Form.Checkbox control={control} name="include_all_time" />
                                    <Form.Label name="include_all_time">
                                        Include all bidders
                                    </Form.Label>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium">💡 Tip</p>
                                <p className="text-xs text-gray-600">
                                    This applies to any bidder registration in the campaigns you
                                    select at any point in time.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            );
        case BranchConditionEnum.DONATION_MADE:
            return (
                <div className="flex flex-col gap-4">
                    <Form.Container>
                        <CampaignSelect path="value.campaigns" />
                    </Form.Container>
                    <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                        {workflowBranchIncludeAllTime ? (
                            <>
                                <p className="text-sm font-medium">⚙️ Configure</p>
                                <p className="text-xs text-gray-600">
                                    This applies to donations made to the campaigns selected after
                                    the contact has entered the workflow.
                                </p>

                                <div className="flex items-center gap-2">
                                    <Form.Checkbox control={control} name="include_all_time" />
                                    <Form.Label name="include_all_time">
                                        Include all time donations
                                    </Form.Label>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium">💡 Tip</p>
                                <p className="text-xs text-gray-600">
                                    This applies to any donation made to the campaigns you select at
                                    any point in time.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            );
        case BranchConditionEnum.TICKET_PURCHASED:
            return (
                <div className="flex flex-col gap-4">
                    <Form.Container>
                        <CampaignSelect path="value.campaigns" resource="event-campaigns" />
                    </Form.Container>
                    <div className="flex flex-col gap-2 rounded-lg border border-gray-100 bg-gray-50 px-4 py-3">
                        {workflowBranchIncludeAllTime ? (
                            <>
                                <p className="text-sm font-medium">⚙️ Configure</p>
                                <p className="text-xs text-gray-600">
                                    This applies to tickets purchased for the campaigns selected
                                    after the contact has entered the workflow.
                                </p>

                                <div className="flex items-center gap-2">
                                    <Form.Checkbox control={control} name="include_all_time" />
                                    <Form.Label name="include_all_time">
                                        Include all time tickets
                                    </Form.Label>
                                </div>
                            </>
                        ) : (
                            <>
                                <p className="text-sm font-medium">💡 Tip</p>
                                <p className="text-xs text-gray-600">
                                    This applies to any ticket purchase in the campaigns you select
                                    at any point in time.
                                </p>
                            </>
                        )}
                    </div>
                </div>
            );
        case BranchConditionEnum.CONTACTS_TAGS:
            return (
                <Form.Container>
                    <Form.Label name="value">The supporter is tagged with...</Form.Label>
                    <div className="flex w-full items-center gap-2">
                        <Form.SingleSelect control={control} name="value.operator">
                            <SingleSelect.Trigger
                                aria-label="Tag operator"
                                className="basis-max-content"
                            />
                            <SingleSelect.Content>
                                <SingleSelect.Item value="any">
                                    <SingleSelect.ItemText>Any</SingleSelect.ItemText>
                                </SingleSelect.Item>
                                <SingleSelect.Item value="all">
                                    <SingleSelect.ItemText>All</SingleSelect.ItemText>
                                </SingleSelect.Item>
                            </SingleSelect.Content>
                        </Form.SingleSelect>
                        <Form.Combobox
                            control={control}
                            name="value.tags"
                            rules={{
                                validate: (values: string[] | undefined) =>
                                    (values ?? []).length > 0 || 'Tags are required',
                            }}
                        >
                            <Combobox.Trigger aria-label="Select tags">
                                <Combobox.Tags placeholder="Search tags" />
                            </Combobox.Trigger>
                            <Combobox.Popover>
                                <Combobox.Search />
                                <Combobox.Items>
                                    {availableTags.map(tag => (
                                        <Combobox.Item value={tag} key={tag}>
                                            <Combobox.ComboboxCheckboxIndicator />
                                            <Combobox.ItemText>
                                                <div className="flex items-center gap-1">
                                                    <FontAwesomeIcon
                                                        icon={faHashtag}
                                                        size="xs"
                                                        className="text-gray-400"
                                                    />
                                                    {tag}
                                                </div>
                                            </Combobox.ItemText>
                                        </Combobox.Item>
                                    ))}
                                </Combobox.Items>
                            </Combobox.Popover>
                        </Form.Combobox>
                    </div>
                    <Form.Error name="value.tags" />
                </Form.Container>
            );
        default:
            return null;
    }
};
export { BranchFormValueInput };
